import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { api } from "../../utils/api";
import { track } from "../../utils/track";
import { C, Primary, Ghost, Note, Sheet, Chip, errText } from "./styles";

/**
 * 수정 요청 (2026-10-09 사장님: '고치기'가 어려움 → 글로 쓰거나, 화면을 캡처해 올리거나, 녹음해서 알려 주면
 * AI 가 듣고 악보를 고친 뒤 무엇을 고쳤는지 알림으로 알려 준다). 처리는 PC 워커가 fix-queue 에서 가져감.
 */
interface IFix {
  id: number;
  text?: string | null;
  status: "queued" | "processing" | "done" | "failed";
  result_note?: string | null;
  has_image: boolean;
  has_audio: boolean;
  created_at: string;
}

const statusText = (s: IFix["status"]) =>
  s === "done" ? <Chip tone="ok">고쳤어요</Chip>
    : s === "failed" ? <Chip tone="err">못 고침</Chip>
      : s === "processing" ? <Chip tone="warn">고치는 중</Chip>
        : <Chip tone="dim">기다리는 중</Chip>;

const canRecord = () => typeof window !== "undefined" && !!(window as any).MediaRecorder && !!navigator.mediaDevices?.getUserMedia;

const FixSheet: React.FC<{ scoreId: number; onClose: () => void }> = ({ scoreId, onClose }) => {
  const imgRef = useRef<HTMLInputElement>(null);
  const audRef = useRef<HTMLInputElement>(null);
  const recRef = useRef<any>(null);
  const chunks = useRef<Blob[]>([]);
  const [text, setText] = useState("");
  const [img, setImg] = useState<File | null>(null);
  const [aud, setAud] = useState<File | null>(null);
  const [recSec, setRecSec] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [list, setList] = useState<IFix[] | null>(null);

  const load = () => api.get(`/my-scores/${scoreId}/fix`).then(({ data }) => setList(data.data)).catch(() => setList([]));
  useEffect(() => { load(); }, [scoreId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 녹음 중이면 1초마다 시간 표시, 3분이면 멈춤
  useEffect(() => {
    if (recSec === null) return;
    if (recSec >= 180) { stopRec(); return; }
    const t = setTimeout(() => setRecSec((s) => (s === null ? null : s + 1)), 1000);
    return () => clearTimeout(t);
  }, [recSec]); // eslint-disable-line react-hooks/exhaustive-deps

  const startRec = async () => {
    setMsg("");
    if (!canRecord()) { audRef.current && audRef.current.click(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const MR = (window as any).MediaRecorder;
      const type = ["audio/mp4", "audio/webm", "audio/ogg"].find((t) => MR.isTypeSupported && MR.isTypeSupported(t)) || "";
      const rec = new MR(stream, type ? { mimeType: type } : undefined);
      chunks.current = [];
      rec.ondataavailable = (e: any) => e.data && e.data.size && chunks.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const mt = rec.mimeType || type || "audio/webm";
        const ext = mt.includes("mp4") ? "m4a" : mt.includes("ogg") ? "ogg" : "webm";
        setAud(new File(chunks.current, `녹음.${ext}`, { type: mt }));
      };
      rec.start();
      recRef.current = rec;
      setRecSec(0);
      track("myscore:fix-record");
    } catch (e) {
      // 마이크를 못 쓰면 휴대폰 녹음 앱으로 녹음한 파일 고르기
      audRef.current && audRef.current.click();
    }
  };
  const stopRec = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    recRef.current = null;
    setRecSec(null);
  };

  const send = async () => {
    if (busy || (!text.trim() && !img && !aud)) return;
    if (recSec !== null) stopRec();
    setBusy(true);
    setMsg("");
    const form = new FormData();
    form.append("text", text.trim());
    if (img) form.append("image", img);
    if (aud) form.append("audio", aud);
    try {
      await api.post(`/my-scores/${scoreId}/fix`, form, { timeout: 5 * 60 * 1000 });
      track("myscore:fix-send");
      setText(""); setImg(null); setAud(null);
      setMsg("보냈어요. AI 가 고친 뒤 무엇을 고쳤는지 알림으로 알려 드려요.");
      load();
    } catch (e) {
      setMsg(errText(e, "보내지 못했어요. 잠시 뒤 다시 시도해 주세요."));
    }
    setBusy(false);
  };

  return (
    <Sheet onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}>
        <h2>수정 요청</h2>
        <Note style={{ marginTop: 0 }}>틀린 곳을 편하게 알려 주세요. 글, 화면 캡처, 녹음 중 하나만 있어도 돼요.</Note>
        <Box>
          <textarea
            id="fix-text"
            value={text}
            maxLength={2000}
            onChange={(e) => setText(e.target.value)}
            placeholder={"예) 2절 첫 줄 가사가 '우채국'이 아니라 '우체국'이에요\n예) 후렴 세 번째 마디 코드는 Am 이 아니라 C 예요"}
            aria-label="고칠 곳"
          />
          <Attach>
            <input ref={imgRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => setImg(e.target.files ? e.target.files[0] : null)} />
            <input ref={audRef} type="file" accept="audio/*" style={{ display: "none" }} onChange={(e) => setAud(e.target.files ? e.target.files[0] : null)} />
            <button type="button" onClick={() => imgRef.current && imgRef.current.click()}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h3l2-3h6l2 3h3v13H4z" /><circle cx="12" cy="13" r="4" /></svg>
              {img ? "사진 바꾸기" : "화면 캡처 넣기"}
            </button>
            {recSec === null ? (
              <button type="button" onClick={startRec}>
                <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>
                {aud ? "다시 녹음" : "녹음하기"}
              </button>
            ) : (
              <button type="button" className="rec" onClick={stopRec}>
                <i /> 녹음 멈추기 {Math.floor(recSec / 60)}:{String(recSec % 60).padStart(2, "0")}
              </button>
            )}
          </Attach>
          {(img || aud) && (
            <Files>
              {img && <span>사진: {img.name} <button type="button" aria-label="사진 빼기" onClick={() => setImg(null)}>×</button></span>}
              {aud && <span>녹음: {aud.name} <button type="button" aria-label="녹음 빼기" onClick={() => setAud(null)}>×</button></span>}
            </Files>
          )}
          <Note style={{ margin: 0 }}>녹음은 맞는 부분을 직접 부르거나 연주해 주세요. AI 가 듣고 맞춰서 고쳐요.</Note>
        </Box>
        <Primary type="button" disabled={busy || (!text.trim() && !img && !aud)} onClick={send}>
          {busy ? "보내는 중…" : "보내기"}
        </Primary>
        {msg && <Note style={{ marginTop: 10, color: C.muted }}>{msg}</Note>}

        {list && list.length > 0 && (
          <History>
            <h3>보낸 요청</h3>
            {list.map((f) => (
              <li key={f.id}>
                <div>
                  {statusText(f.status)}
                  <em>{f.created_at.slice(5, 16).replace("-", "/")}</em>
                </div>
                <p>{f.text || [f.has_image && "화면 캡처", f.has_audio && "녹음"].filter(Boolean).join(" · ")}</p>
                {f.result_note && <p className="ans">{f.result_note}</p>}
              </li>
            ))}
          </History>
        )}
        <Ghost type="button" style={{ width: "100%", marginTop: 12 }} onClick={onClose}>닫기</Ghost>
      </div>
    </Sheet>
  );
};

export default FixSheet;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 12px 0 14px;
  & > textarea {
    min-height: 96px;
    padding: 12px;
    border-radius: 12px;
    border: 1px solid ${C.line};
    background: ${C.card};
    color: ${C.fg};
    font: inherit;
    font-size: 15px;
    line-height: 1.5;
    resize: vertical;
    box-sizing: border-box;
    width: 100%;
  }
`;

const Attach = styled.div`
  display: flex;
  gap: 8px;
  & > button {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 44px;
    border-radius: 12px;
    border: 1px solid ${C.line};
    background: ${C.card};
    color: ${C.fg};
    font-size: 14px;
    cursor: pointer;
  }
  & svg {
    width: 18px;
    height: 18px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linejoin: round;
    stroke-linecap: round;
  }
  & > button.rec {
    border-color: ${C.err};
    color: ${C.err};
  }
  & > button.rec > i {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${C.err};
  }
`;

const Files = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: ${C.muted};
  & button {
    border: 0;
    background: none;
    color: ${C.muted};
    font-size: 16px;
    cursor: pointer;
    padding: 0 6px;
  }
  & span {
    overflow-wrap: anywhere;
  }
`;

const History = styled.ul`
  list-style: none;
  margin: 18px 0 0;
  padding: 0;
  & > h3 {
    margin: 0 0 8px;
    font-size: 14px;
    color: ${C.muted};
  }
  & > li {
    padding: 10px 0;
    border-top: 1px solid ${C.line};
  }
  & > li > div {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  & em {
    font-style: normal;
    font-size: 12px;
    color: ${C.dim};
  }
  & p {
    margin: 6px 0 0;
    font-size: 14px;
    overflow-wrap: anywhere;
    white-space: pre-line;
  }
  & p.ans {
    color: ${C.fg};
    background: ${C.card};
    border-radius: 10px;
    padding: 8px 10px;
  }
`;
